/**
 * AttendanceGate — wraps the app for non-super-admin users:
 *   1. On mount: fetches /attendance/me/today and opens the "Start Your Day"
 *      modal if attendance hasn't been marked yet.
 *   2. Installs an axios response interceptor that re-opens the modal on HTTP 423
 *      (covers both "not marked" and "currently inactive" cases).
 *   3. Schedules an auto-logout at the next 7:30 PM IST or midnight IST. Auto-logout
 *      calls mark-inactive via the AuthContext logout() to close the open session.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { CheckCircle2, Loader2, Clock, PauseCircle, PlayCircle } from "lucide-react";
import { API, useAuth } from "../App";

const SUPER_ADMIN_EMAIL = "admin@locofast.com";

// IST helpers — compute IST "now" from local Date by offsetting against UTC
const istNow = () => {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utcMs + 5.5 * 3600 * 1000);
};

// Schedule next logout time in local Date — next 7:30 PM IST or midnight IST after now
const nextLogoutDate = () => {
  const ist = istNow();
  const today = new Date(ist);
  today.setHours(19, 30, 0, 0);
  const todayMidnight = new Date(ist);
  todayMidnight.setHours(0, 0, 0, 0);
  todayMidnight.setDate(todayMidnight.getDate() + 1);

  let targetIst;
  if (ist < today) targetIst = today;
  else if (ist < todayMidnight) targetIst = todayMidnight;
  else { targetIst = new Date(todayMidnight); targetIst.setDate(targetIst.getDate() + 1); }

  const utcTargetMs = targetIst.getTime() - 5.5 * 3600 * 1000 - new Date().getTimezoneOffset() * 60000;
  return new Date(utcTargetMs);
};

// Broadcast helper — anyone toggling attendance can dispatch this so the header badge refreshes immediately
const ATTENDANCE_EVENT = "lf-attendance-changed";
const broadcastAttendanceChange = () => {
  try { window.dispatchEvent(new CustomEvent(ATTENDANCE_EVENT)); } catch (_) { /* ssr safety */ }
};

// Format seconds as "Hh MMm" or "MMm SSs" (compact, live-timer friendly)
export const formatActiveDuration = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
};

const AttendanceGate = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const interceptorRef = useRef(null);
  const logoutTimerRef = useRef(null);

  const isApplicable = user && user.email !== SUPER_ADMIN_EMAIL && (user.role === "user" || ['admin','super_admin'].includes(user.role));

  const fetchAttendance = useCallback(async () => {
    if (!isApplicable) return null;
    try {
      const res = await axios.get(`${API}/attendance/me/today`);
      setAttendance(res.data);
      return res.data;
    } catch (_) {
      return null;
    }
  }, [isApplicable]);

  useEffect(() => {
    if (!user) {
      if (interceptorRef.current !== null) axios.interceptors.response.eject(interceptorRef.current);
      interceptorRef.current = null;
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
      return;
    }
    if (!isApplicable) return;

    let cancelled = false;
    (async () => {
      const data = await fetchAttendance();
      if (cancelled || !data) return;
      // Open if not marked yet, OR if marked but currently inactive (mid-day pause)
      if (data.is_required && (!data.marked_active || data.current_state === "inactive")) {
        setOpen(true);
      }
    })();

    interceptorRef.current = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 423) {
          setOpen(true);
          fetchAttendance();
          toast.error(err.response.data?.detail || "Please mark yourself active.", { duration: 5000 });
        }
        return Promise.reject(err);
      }
    );

    // Auto-logout at next 7:30 PM IST or midnight IST — logout() closes open session
    const target = nextLogoutDate();
    const ms = Math.max(1000, target.getTime() - Date.now());
    logoutTimerRef.current = setTimeout(() => {
      toast.info("Session ended (7:30 PM IST). Please login again.", { duration: 8000 });
      logout();
    }, ms);

    return () => {
      cancelled = true;
      if (interceptorRef.current !== null) axios.interceptors.response.eject(interceptorRef.current);
      interceptorRef.current = null;
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Action handler — used for both "Mark Active for today" and "Resume Work"
  const handleAction = async () => {
    setSubmitting(true);
    try {
      const inactiveMidday = attendance?.marked_active && attendance?.current_state === "inactive";
      const endpoint = inactiveMidday ? "toggle" : "mark-active";
      const res = await axios.post(`${API}/attendance/${endpoint}`);
      if (inactiveMidday) {
        toast.success("You're Active again");
      } else {
        const msg = res.data?.marked_late
          ? `Marked active (late at ${new Date(res.data.marked_active_at).toLocaleTimeString()})`
          : "Marked active for today";
        toast.success(msg);
      }
      await fetchAttendance();
      broadcastAttendanceChange();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  const inactiveMidday = attendance?.marked_active && attendance?.current_state === "inactive";

  return (
    <>
      {children}
      {isApplicable && (
        <Dialog open={open} onOpenChange={(o) => {
          // Block close while required action pending
          if (!attendance?.marked_active && attendance?.is_required) return;
          if (inactiveMidday) return; // must resume to keep working
          setOpen(o);
        }}>
          <DialogContent className="sm:max-w-md" data-testid="mark-active-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {inactiveMidday ? (
                  <PlayCircle className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Clock className="w-5 h-5 text-blue-600" />
                )}
                {inactiveMidday ? "Resume Work" : "Start Your Day"}
              </DialogTitle>
              <DialogDescription>
                {inactiveMidday
                  ? "You're currently marked Inactive. Click below to resume and continue working in the CRM."
                  : attendance?.past_cutoff
                    ? "It's past 11 AM IST. You haven't been marked active today, so you can't take any action in the CRM yet. Click below to mark yourself active and continue."
                    : "Mark yourself active to be eligible for today's lead auto-allocation. (Cut-off: 11:00 AM IST)"}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 space-y-1">
                <p>Logged in as <strong>{user?.name}</strong> ({user?.email})</p>
                {attendance?.now_ist && (
                  <p className="text-xs text-slate-500">Current time IST: {new Date(attendance.now_ist).toLocaleString()}</p>
                )}
                {inactiveMidday && attendance?.total_active_seconds > 0 && (
                  <p className="text-xs text-slate-500">Active time so far today: <strong>{formatActiveDuration(attendance.total_active_seconds)}</strong></p>
                )}
              </div>
              <Button
                onClick={handleAction}
                disabled={submitting}
                className={inactiveMidday ? "w-full bg-emerald-600 hover:bg-emerald-700" : "w-full bg-blue-600 hover:bg-blue-700"}
                data-testid="mark-active-confirm-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {inactiveMidday ? "Resume — Mark Me Active" : "Mark Me Active for Today"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Header toggle — visible to every agent/admin (except super admin).
//   State 1: not marked today → amber "Mark Active" button (opens modal).
//   State 2: marked + active → emerald pill "Active · 02h 15m", click toggles to inactive.
//   State 3: marked + inactive → slate pill "Inactive · resume", click toggles to active.
export const AttendanceBadge = () => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Local tick to drive the live timer without re-fetching every second
  const [tick, setTick] = useState(0);

  const isApplicable = user && user.email !== SUPER_ADMIN_EMAIL && (user.role === "user" || ['admin','super_admin'].includes(user.role));

  const load = useCallback(async () => {
    if (!isApplicable) return;
    try {
      const res = await axios.get(`${API}/attendance/me/today`);
      setState({ ...res.data, _fetchedAt: Date.now() });
    } catch (_) { /* ignore */ }
  }, [isApplicable]);

  useEffect(() => {
    if (!isApplicable) return;
    load();
    const refresh = setInterval(load, 30000); // re-sync from server every 30s
    const ticker = setInterval(() => setTick((t) => t + 1), 1000); // 1s timer tick
    const onChange = () => load();
    window.addEventListener("lf-attendance-changed", onChange);
    return () => {
      clearInterval(refresh);
      clearInterval(ticker);
      window.removeEventListener("lf-attendance-changed", onChange);
    };
  }, [isApplicable, load]);

  const handleToggle = async () => {
    if (!state?.marked_active) return; // handled by gate modal
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/attendance/toggle`);
      toast.success(res.data?.message || "Status updated");
      await load();
      broadcastAttendanceChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle status");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isApplicable || !state || !state.is_required) return null;

  // Compute live seconds: server returns total (includes ongoing at fetch time);
  // we extend by ticker delta since fetch when currently active.
  let liveSeconds = state.total_active_seconds || 0;
  if (state.current_state === "active" && state._fetchedAt) {
    liveSeconds += Math.max(0, Math.floor((Date.now() - state._fetchedAt) / 1000));
  }
  // touch tick so React keeps re-rendering once per second
  void tick;

  // Case A: not marked today
  if (!state.marked_active) {
    return (
      <button
        onClick={async () => {
          setSubmitting(true);
          try {
            await axios.post(`${API}/attendance/mark-active`);
            await load();
            broadcastAttendanceChange();
            toast.success("Marked active for today");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to mark active");
          } finally { setSubmitting(false); }
        }}
        disabled={submitting}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 disabled:opacity-60"
        data-testid="attendance-badge-mark"
        title="Mark yourself active for today"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
        Mark Active
      </button>
    );
  }

  // Case B: marked + currently active → green pill with timer, click to pause
  if (state.current_state === "active") {
    return (
      <button
        onClick={handleToggle}
        disabled={submitting}
        className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors disabled:opacity-60"
        data-testid="attendance-toggle-active"
        title="Click to mark Inactive"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        <span>Active</span>
        <span className="text-emerald-600/80 tabular-nums">· {formatActiveDuration(liveSeconds)}</span>
        <PauseCircle className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity ml-0.5" />
      </button>
    );
  }

  // Case C: marked + inactive → slate/orange pill, click to resume
  return (
    <button
      onClick={handleToggle}
      disabled={submitting}
      className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 transition-colors disabled:opacity-60"
      data-testid="attendance-toggle-inactive"
      title="Click to mark Active again"
    >
      <PauseCircle className="w-3.5 h-3.5" />
      <span>Inactive</span>
      {state.total_active_seconds > 0 && (
        <span className="text-orange-600/80 tabular-nums">· {formatActiveDuration(state.total_active_seconds)} today</span>
      )}
      <PlayCircle className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity ml-0.5" />
    </button>
  );
};

export default AttendanceGate;
